import { Button } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import Label from "components/Label";
import useFirestore from "hooks/useFirestore";
import { PATH_DASHBOARD } from "routes/paths";
import type { SxProps } from "@material-ui/system";
import type { Theme } from "@material-ui/core";
import type { User } from "../../../@types/sharedSchema";

/**
 * MAY THIS USER OPEN `/dashboard/crm` — the one rule, shared by every surface that links there.
 *
 * The same `crm` feature the old contacts page is gated on: the ROUTE sits behind
 * `FeatureGate feature="crm"` (see `routes/index.tsx`), so a link is shown exactly when its
 * destination is actually reachable. (Until 2026-09-11 a separate `crmPreview` pilot flag was
 * required as well; the page is open to every `crm` holder since.) The main-page promo
 * (`CrmBetaPromoBanner`) and the sidebar entry (`useContactsSidebarPath`) read this too, so
 * none of them can disagree about who may go there.
 */
export const canReachCrmPage = (userSettings: Pick<User, "advancedFeatures"> | null | undefined): boolean =>
  userSettings?.advancedFeatures?.includes("crm") ?? false;

/**
 * The one-click hop between the two contacts surfaces.
 *
 * Both pages are called "לקוחות" now — the CRM sidebar entry is gone, so from the old page
 * this button is the way to `/dashboard/crm` (besides the main-page promo), and from the
 * new page it is the only way back. The old page stays one click away for as long as the
 * two run side by side.
 *
 * Styling is copied from the contact-detail header's back affordance
 * (`detail/ContactDetailHeader.tsx`) — `color="inherit"` + `sx.color: text.secondary` on
 * the default (text) Button variant. That triplet is this codebase's idiom for a
 * low-emphasis grey text button; `variant="text"` on its own leaves the label
 * primary-coloured, i.e. loud.
 */

type ContactsPageSwitchButtonProps = {
  /**
   * Which page to send the user to — i.e. the OPPOSITE of the page rendering this.
   * `"new"` is gated (see below); `"old"` never is, since `/dashboard/contacts` is
   * reachable by every customer.
   */
  destination: "new" | "old";
  sx?: SxProps<Theme>;
};

export function ContactsPageSwitchButton({
  destination,
  sx
}: ContactsPageSwitchButtonProps): JSX.Element | null {
  const { userSettings, isLoadingUser } = useFirestore();
  const navigate = useNavigate();

  const goingToNewPage = destination === "new";

  // Render nothing until the user document has actually resolved. `isLoadingUser` starts
  // FALSE and `userSettings` starts null, so a plain `includes()` check would already be
  // falsy on the first paint — but reading both keeps the visibility change MONOTONIC
  // (hidden → shown, never shown → hidden). A button that appears and then vanishes
  // reads as a bug, and this is the same guard `guards/FeatureGate.tsx` applies before
  // it decides whether to admit or refuse.
  if (goingToNewPage && (isLoadingUser || !userSettings)) return null;

  // See `canReachCrmPage` — the button is visible exactly when the destination is reachable.
  if (goingToNewPage && !canReachCrmPage(userSettings)) return null;

  return (
    <Button
      color="inherit"
      size="small"
      onClick={() =>
        navigate(goingToNewPage ? PATH_DASHBOARD.general.crm : PATH_DASHBOARD.general.contacts)
      }
      /* The beta launch's "חדש" chip — only on the way IN. The way back is not news. Rides
         `endIcon` so the button stays one self-contained inline-flex item in both of its hosts
         (a column Stack on the old page, a centred row in the CRM header). */
      endIcon={
        goingToNewPage ? (
          <Label color="success" variant="filled" sx={{ color: "common.white", height: 20 }}>
            חדש
          </Label>
        ) : undefined
      }
      sx={{
        color: "text.secondary",
        // The label is a full phrase; wrapping it mid-sentence looks broken at 390px.
        whiteSpace: "nowrap",
        // Small to the EYE, not to the thumb — most of these users are on phones.
        minHeight: { xs: 44, sm: 32 },
        // The small Button forces `fontSize: 18` onto its first endIcon child with a
        // (0,2,0) selector, which beats the Label's own 12px AND a plain `sx` on it —
        // the chip's glyphs would overflow its 20px box. Overridden at the same
        // specificity, here, where it can win.
        "& .MuiButton-endIcon > *:nth-of-type(1)": { fontSize: 11 },
        // NO `alignSelf` here on purpose: the two hosts are opposite flex axes. The old
        // page mounts this in a COLUMN Stack (needs `flex-start`, or the button stretches
        // the full page width); the CRM header bar is a ROW with `alignItems: center`
        // (where `flex-start` would top-align it against the create-contact button). Each
        // call site passes what its own container needs.
        ...sx
      }}
    >
      {goingToNewPage ? "נסיון עמוד לקוחות חדש" : "חזרה לעמוד לקוחות ישן"}
    </Button>
  );
}

export default ContactsPageSwitchButton;
